import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../hooks";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import {
  FlowTestForm,
  getFlowTestFormSchema,
} from "../../types/forms/flowTest";
import { FlowTest } from "../../types/model/flowTest";
import FormAutocomplete from "../General/FormAutocomplete";

interface FlowTestInfoProps {
  flowTestData: FlowTest;
  canEdit: boolean;
  onSubmit: (values: FlowTestForm) => Promise<void>;
}

export default function FlowTestInfo({
  flowTestData,
  canEdit,
  onSubmit,
}: Readonly<FlowTestInfoProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getFlowTestFormSchema(t);

  const methods = useForm<FlowTestForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      testTypeId: flowTestData.testTypeId ?? null,
      startDate: flowTestData.startDate ?? undefined,
      aquiferId: flowTestData.aquiferId ?? null,
    },
  });

  const { data: flowTestTypes = [] } = useReferenceData(
    "flow_test",
    "flow_test_types"
  );
  const { data: aquiferSystems = [] } = useReferenceData(
    "hydro",
    "aquifer_systems"
  );

  const handleFormSubmit = async (values: FlowTestForm) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    methods.reset({
      testTypeId: flowTestData.testTypeId ?? null,
      startDate: flowTestData.startDate ?? undefined,
      aquiferId: flowTestData.aquiferId ?? null,
    });
  }, [flowTestData]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          {/* Test Information */}
          <Grid2 size={{ xs: 12 }}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Grid2 size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography variant="h5">
                      {t("flow-tests.sections.test-information")}
                    </Typography>

                    {/* Action Icons */}
                    {isEditing && canEdit ? ( 
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title={t("generic.save")}>
                          <IconButton
                            color="success"
                            onClick={methods.handleSubmit(handleFormSubmit)}
                            disabled={isSaving}
                          >
                            {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t("generic.cancel")}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              setIsEditing(false);
                              methods.reset();
                            }}
                          >
                            <EditOffIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      canEdit && (
                        <Tooltip title={t("generic.edit")}>
                          <IconButton
                            color="primary"
                            onClick={() => setIsEditing(true)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </Box>
                </Grid2>
                <Grid2 container spacing={2} mt={1}>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <FormAutocomplete
                      name="testTypeId"
                      label={t("flow-tests.flow-test.test-type")}
                      options={flowTestTypes}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Controller
                        name="startDate"
                        control={methods.control}
                        render={({ field }) => (
                          <DatePicker
                            label={t("flow-tests.flow-test.start-date")}
                            value={
                              field.value
                                ? dayjs(field.value, "YYYY/MM/DD")
                                : null
                            }
                            onChange={(date) => {
                              field.onChange(
                                date ? date.format("YYYY/MM/DD") : undefined
                              );
                            }}
                            format="DD/MM/YYYY"
                            disabled={!isEditing || isSaving}
                            sx={{ ...formStyles }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                variant: isEditing ? "outlined" : "standard",
                                placeholder: "DD/MM/YYYY",
                                error: !!methods.formState.errors.startDate,
                                helperText:
                                  methods.formState.errors.startDate?.message,
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <FormAutocomplete
                      name="aquiferId"
                      label={t("flow-tests.flow-test.aquifer")}
                      options={aquiferSystems}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      disabled={false}
                      formStyles={formStyles}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
